import { useMutation, useQuery } from "@tanstack/react-query";
import { downloadBlob, useApi } from "@csa/web-foundation";
import type { AdminPlatformResponse, MetricsQuery } from "../../api/admin-platform-contracts";

// Platform "Panel" read (#204). The panel is an AGGREGATOR of ONE composed, read-only
// rollup — GET /v1/metrics/admin-platform — that the backend derives across the frozen
// contexts (curation, config governance, the customer-#1 funnel, KVKK/compliance,
// operational health, the pilot scorecard). NO client-side aggregation, NO fabricated
// numbers: the surface renders the server rollup verbatim; a source with no rows is a
// real zero / empty (present) series and a genuinely absent surface (DSAR) is an honest
// null the UI renders as a dignified "no source" note — never invented.

const METRICS = ["metrics"];

/** Serialize the shared cohort/window bounds to the query string the controller reads;
 *  blank bounds are omitted → the read treats them as unbounded (all-time / trailing 30
 *  days for the consent day-series). */
function toQueryString(q: MetricsQuery): string {
  const params = new URLSearchParams();
  if (q.cohortFrom) params.set("cohortFrom", q.cohortFrom);
  if (q.cohortTo) params.set("cohortTo", q.cohortTo);
  if (q.from) params.set("from", q.from);
  if (q.to) params.set("to", q.to);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** GET /v1/metrics/admin-platform — the composed platform-health rollup (all-scope;
 *  analytics:read @ all; honest-empty). The bounds are the cache key so a filter change
 *  refetches. The panel passes an empty query (all-time) by default. */
export function useAdminPlatform(q: MetricsQuery = {}, enabled = true) {
  const api = useApi();
  return useQuery({
    enabled,
    queryKey: [
      ...METRICS,
      "admin-platform",
      q.cohortFrom ?? null,
      q.cohortTo ?? null,
      q.from ?? null,
      q.to ?? null,
    ],
    queryFn: () => api.get<AdminPlatformResponse>(`/v1/metrics/admin-platform${toQueryString(q)}`),
  });
}

/**
 * A12 · GET /v1/metrics/admin-platform/export — the SAME rollup as CSV.
 *
 * A mutation rather than a query on purpose: a file download is an act the
 * operator performs, not state the surface holds. Modelling it as a query would
 * mean react-query caching a Blob and potentially re-downloading on refocus.
 *
 * The filename carries the applied bounds, because a folder of files all called
 * `csa-platform-metrics.csv` is indistinguishable a week later — the export is
 * only useful if you can tell which window it covers.
 */
export function useExportAdminPlatform() {
  const api = useApi();
  return useMutation({
    mutationFn: async (q: MetricsQuery = {}) => {
      const blob = await api.getBlob(`/v1/metrics/admin-platform/export${toQueryString(q)}`, {
        accept: "text/csv",
      });
      const stamp = [q.cohortFrom, q.cohortTo, q.from, q.to].filter(Boolean).join("_");
      downloadBlob(blob, `csa-platform-metrics${stamp ? `_${stamp}` : ""}.csv`);
    },
  });
}
